import { ArrowLeftRight, X } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { UploadPlaceholder } from "@/components/flowly/infra/UploadPlaceholder/UploadPlaceholder";
import { ACCEPTED_IMAGE_TYPES_STRING } from "@/config/media";

interface VideoSourceUploadProps {
  supportsEndFrame: boolean;
  fileInputRef: React.RefObject<HTMLInputElement>;
  filePreview: string | null;
  endFramePreview?: string | null;
  onInputChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFile: () => void;
  onDrop: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  onEndFrameUpload?: () => void;
  onEndFrameClear?: () => void;
  onSwapFrames?: () => void;
}

export const VideoSourceUpload = ({
  supportsEndFrame,
  fileInputRef,
  filePreview,
  endFramePreview,
  onInputChange,
  onClearFile,
  onDrop,
  onDragOver,
  onDragLeave,
  onEndFrameUpload,
  onEndFrameClear,
  onSwapFrames,
}: VideoSourceUploadProps) => {
  const { t } = useLanguage();
  // Upload is local-only — the hook reads the file into a data URL and the
  // R2 push happens later, inside the gated `generateVideo` action. So the
  // picker can be opened by anyone.
  const openPicker = () => fileInputRef.current?.click();

  if (supportsEndFrame) {
    return (
      <div className="flex flex-col gap-4 max-w-[520px]">
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES_STRING}
          onChange={onInputChange}
          className="hidden"
        />
        {/* Titles row */}
        <div className="flex gap-4">
          <h3 className="flex-1 text-foreground font-bold text-base md:text-lg">
            {t.generate.firstFrame}
          </h3>
          {/* Spacer matching swap button width */}
          <div className="w-8" />
          <h3 className="flex-1 text-foreground font-bold text-base md:text-lg">
            {t.generate.endFrame}
          </h3>
        </div>

        {/* Boxes + swap button row */}
        <div className="flex items-center gap-4">
          {/* First Frame */}
          <div className="flex-1">
            <UploadPlaceholder
              src={filePreview}
              label={t.generate.firstFrame}
              alt="First Frame"
              onClick={() => !filePreview && openPicker()}
              onDrop={onDrop}
              onDragOver={onDragOver}
              onDragLeave={onDragLeave}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onClearFile();
                }}
                className="absolute top-2 left-2 p-1 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </UploadPlaceholder>
          </div>

          {/* Swap button */}
          <button
            onClick={() => onSwapFrames?.()}
            disabled={!filePreview && !endFramePreview}
            className="p-1.5 rounded-full hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
            title={t.generate.swapFrames}
          >
            <ArrowLeftRight className="w-5 h-5 text-muted-foreground" />
          </button>

          {/* End Frame */}
          <div className="flex-1">
            <UploadPlaceholder
              src={endFramePreview}
              label={t.generate.endFrame}
              alt="End Frame"
              onClick={() => onEndFrameUpload?.()}
            >
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEndFrameClear?.();
                }}
                className="absolute top-2 left-2 p-1 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </UploadPlaceholder>
          </div>
        </div>
      </div>
    );
  }

  // Single source image mode
  return (
    <div className="flex flex-col gap-4">
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_IMAGE_TYPES_STRING}
        onChange={onInputChange}
        className="hidden"
      />
      <UploadPlaceholder
        src={filePreview}
        label={t.generate.dragImageHere}
        title={t.generate.sourceImage}
        alt="Source"
        onClick={() => !filePreview && openPicker()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <button
          onClick={(e) => {
            e.stopPropagation();
            onClearFile();
          }}
          className="absolute top-2 left-2 p-1 rounded-full bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </UploadPlaceholder>
    </div>
  );
};
